import { RecommendationIntent, RecommendationResourceType } from '@coach-bot/data-access/shared/enums';
import { LearnedRecommendation, Recommendation } from '@coach-bot/data-access/shared/interfaces';
import {
  Student,
  StudentEvent,
  StudentRecommendation,
  StudentThirdPartyAppCredentials,
} from '@coach-bot/data-access/shared/models';
import { AbstractStudentStore } from '@coach-bot/data-access/shared/stores/abstract/abstract-student.store';
import { StudentService } from '@coach-bot/data-access/student/services/student.service';
import { AnalyticsEvent, AnalyticsService, getRecommendationResourceIds } from '@coach-bot/features/analytics';
import { LEARNING_APPS_WITH_CREDENTIALS } from '@shared/utils';
import { Mutex } from 'async-mutex';
import { BehaviorSubject, Observable, Subject, filter, firstValueFrom, take } from 'rxjs';
import { DEFAULT_WATCH_RECOMMENDATIONS_INTERVAL_MS, DEFAULT_WATCH_STUDENT_BALANCE_INTERVAL_MS } from '../constants';
import { getIXLQuizId } from '../utils';

const DEFAULT_SUBJECT = 'Math';
const EDULASTIC_APP_NAME = 'Edulastic';
const IXL_APP_NAME = 'IXL';
const LEARNED_RESOURCE_MASTERED_PERCENTAGE = 100;
const NUMBER_OF_RECOMMENDATIONS_TO_LOAD = 10;

export class StudentState {
  readonly showLearningReminder$ = new Subject<boolean>();

  private handleNewStudentBalanceMutex = new Mutex();

  constructor(
    private analyticsService: AnalyticsService,
    private email$: BehaviorSubject<string | null>,
    private studentService: StudentService,
    private studentStore: AbstractStudentStore,
    private userId$: BehaviorSubject<string | null>
  ) {}

  async loadStudent(userId: string): Promise<Student> {
    try {
      const student: Student = await this.studentService.getStudent(userId!);
      await this.studentStore.setStudent(student);
      return student;
    } catch (err: unknown) {
      console.error('Load student error:', err);
    }
  }

  async loadRecommendations(): Promise<void> {
    let currentRecommendations: Recommendation[] | null;
    let recommendationsLoadingFailed: boolean;
    try {
      currentRecommendations = this.studentStore.recommendations$.value;
      recommendationsLoadingFailed = this.studentStore.recommendationsLoadingFailed$.value;
      if (recommendationsLoadingFailed) {
        recommendationsLoadingFailed = false;
        await this.studentStore.setRecommendationsLoadingFailed(recommendationsLoadingFailed);
      }
      const student: Student = await firstValueFrom(
        this.studentStore.student$.pipe(
          filter(student => !!student),
          take(1)
        )
      );
      let studentRecommendations: StudentRecommendation[] = await this.studentService.getRecommendations(
        student.student_id,
        DEFAULT_SUBJECT,
        { apps: [IXL_APP_NAME], externalTests: [EDULASTIC_APP_NAME], internalTests: [IXL_APP_NAME] },
        [IXL_APP_NAME],
        NUMBER_OF_RECOMMENDATIONS_TO_LOAD,
        true
      );
      if (studentRecommendations) {
        // Need to show only not learned recommendations
        const learnedRecommendations: LearnedRecommendation[] | null = this.studentStore.learnedRecommendations$.value;
        studentRecommendations = this.filterStudentRecommendations(studentRecommendations, learnedRecommendations);
        studentRecommendations = studentRecommendations.slice(0, 1);
        const recommendations: Recommendation[] = this.mapRecommendations(studentRecommendations);
        if (JSON.stringify(currentRecommendations) !== JSON.stringify(recommendations)) {
          await this.studentStore.setRecommendations(recommendations);
          this.studentStore.setWatchRecommendationsIntervalMs(DEFAULT_WATCH_RECOMMENDATIONS_INTERVAL_MS);
        }
      }
    } catch (err: unknown) {
      console.error('Load recommendations error:', err);
      if (!currentRecommendations && !recommendationsLoadingFailed) {
        await this.studentStore.setRecommendationsLoadingFailed(true);
      }
    }
  }

  async loadStudentBalance(): Promise<number> {
    try {
      const studentId: string = await firstValueFrom(
        this.studentStore.studentId$.pipe(
          filter(studentId => !!studentId),
          take(1)
        )
      );
      const newStudentBalance: number = await this.studentService.getStudentBalance(studentId);
      await this.handleNewStudentBalance(newStudentBalance);
      return newStudentBalance;
    } catch (err: unknown) {
      console.error('Load student balance error:', err);
    }
  }

  async handleNewStudentBalance(newStudentBalance: number): Promise<void> {
    const release = await this.handleNewStudentBalanceMutex.acquire();
    try {
      const currentStudentBalance: number | null = this.studentStore.studentBalance$.value;
      if (!this.studentStore.studentId$.value || currentStudentBalance === newStudentBalance) {
        return;
      }
      await this.studentStore.setStudentBalance(newStudentBalance);
      if (currentStudentBalance === null) {
        return;
      }
      const difference: number = newStudentBalance - currentStudentBalance;
      if (difference > 0) {
        this.studentStore.setMoneyReward(difference);
        this.studentStore.setWatchStudentBalanceIntervalMs(DEFAULT_WATCH_STUDENT_BALANCE_INTERVAL_MS);
        // Reset to avoid showing old recommendations and display loader
        await this.studentStore.setRecommendations(null);
        await this.studentStore.setWaitingForRewardRecommendation(null);
        this.loadRecommendations();
        const [learningResourceId, placementQuizId]: [string, string] = getRecommendationResourceIds(
          this.studentStore.waitingForRewardRecommendation$.value
        );
        await this.analyticsService.track(AnalyticsEvent.GetReward, {
          email: this.email$.value,
          userId: this.userId$.value,
          studentId: this.studentStore.studentId$.value,
          amount: difference,
          newBalance: newStudentBalance,
          learningResourceId,
          placementQuizId,
        });
      }
    } finally {
      release();
    }
  }

  async loadThirdPartyAppCredentialsList(): Promise<void> {
    try {
      const promises: Array<Promise<StudentThirdPartyAppCredentials>> = LEARNING_APPS_WITH_CREDENTIALS.map(
        (appName: string) => this.studentService.getThirdPartyAppCredentials(appName)
      );
      let thirdPartyAppCredentialsList: StudentThirdPartyAppCredentials[] = await Promise.all(promises);
      thirdPartyAppCredentialsList = thirdPartyAppCredentialsList.filter(
        (item: StudentThirdPartyAppCredentials) => !!item.username && !!item.password
      );
      await this.studentStore.setThirdPartyAppCredentialsList(thirdPartyAppCredentialsList);
    } catch (err: unknown) {
      console.error('Load third party app credentials list error:', err);
    }
  }

  async loadLastLearningActivityTime(): Promise<number | null> {
    try {
      const studentId: string = await firstValueFrom(
        this.studentStore.studentId$.pipe(
          filter(studentId => !!studentId),
          take(1)
        )
      );
      const lastLearningActivityTime: number | null =
        await this.studentService.getLastStudentLearningActivityTime(studentId);
      if (
        !!lastLearningActivityTime &&
        this.studentStore.lastLearningActivityTime$.value !== lastLearningActivityTime &&
        (!this.studentStore.lastLearningActivityTime$.value ||
          lastLearningActivityTime > this.studentStore.lastLearningActivityTime$.value)
      ) {
        await this.studentStore.setLastLearningActivityTime(lastLearningActivityTime);
      }
      return lastLearningActivityTime;
    } catch (err: unknown) {
      console.error('Load last learning activity time error:', err);
    }
  }

  filterRecommendations(
    recommendations: Recommendation[],
    learnedRecommendations: LearnedRecommendation[] | null
  ): Recommendation[] {
    if (!learnedRecommendations) {
      return recommendations;
    }
    return recommendations.filter((recommendation: Recommendation) => {
      return !learnedRecommendations.some(
        (learnedRecommendation: LearnedRecommendation) =>
          learnedRecommendation.resourceUrl === recommendation.url ||
          learnedRecommendation.resourceId === recommendation.id
      );
    });
  }

  subscribeToStudentEvents(studentId: string): Observable<StudentEvent> {
    return this.studentService.subscribeToStudentEvents(studentId);
  }

  private filterStudentRecommendations(
    studentRecommendations: StudentRecommendation[],
    learnedRecommendations: LearnedRecommendation[] | null
  ): StudentRecommendation[] {
    return studentRecommendations.filter((item: StudentRecommendation) => {
      const isNotLearned: boolean =
        !learnedRecommendations ||
        !learnedRecommendations.some(
          (learnedRecommendation: LearnedRecommendation) =>
            learnedRecommendation.resourceUrl === (item.resource?.url || item.test?.url) ||
            (learnedRecommendation.resourceId &&
              learnedRecommendation.resourceId === item.resource?.appLevel?.thirdPartyLevelId)
        );
      const isValidResource: boolean =
        item.intent === RecommendationIntent.Learn &&
        item.type === RecommendationResourceType.Resource &&
        !!item.resource &&
        !!item.resource.course &&
        item.resource.masteryPercentage !== LEARNED_RESOURCE_MASTERED_PERCENTAGE &&
        !!item.resource.url &&
        isNotLearned;
      if (isValidResource) {
        return true;
      }
      const isValidTest: boolean =
        item.intent === RecommendationIntent.Test &&
        !!item.test &&
        item.test.app === IXL_APP_NAME &&
        !!item.test.url &&
        isNotLearned;
      return isValidTest;
    });
  }

  private mapRecommendations(recommendations: StudentRecommendation[]): Recommendation[] {
    return recommendations.map((item: StudentRecommendation) => {
      let isPlacementTest: boolean = false;
      if (item.test) {
        const testName: string = item.test.name.toLowerCase();
        isPlacementTest = testName.includes('placement') && testName.includes('test');
      }
      return {
        grade: item.resource?.course?.grade,
        id: item.test ? getIXLQuizId(item.test.url) : item.resource?.appLevel?.thirdPartyLevelId,
        intent: item.intent,
        isPlacementTest,
        name: item.resource?.name || item.test?.name || '',
        type: item.type,
        url: item.resource?.url || item.test?.url || '',
      };
    });
  }
}
